import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  PresentationChartLineIcon,
  CalendarDaysIcon,
  RectangleStackIcon,
  ArrowTrendingUpIcon,
  DocumentArrowDownIcon,
  BuildingLibraryIcon,
  ChartBarSquareIcon,
  ShoppingBagIcon,
  GlobeEuropeAfricaIcon
} from '@heroicons/react/24/outline';

// Import des composants d'analyse
import StockRotationAnalysis from '../../components/analytics/StockRotationAnalysis';
import StorageCostsAnalysis from '../../components/analytics/StorageCostsAnalysis';
import DemandForecastAnalysis from '../../components/analytics/DemandForecastAnalysis';
import SeasonalityAnalysis from '../../components/analytics/SeasonalityAnalysis';
import ProductPerformanceAnalysis from '../../components/analytics/ProductPerformanceAnalysis';
import MarketComparisonAnalysis from '../../components/analytics/MarketComparisonAnalysis';

const AdvancedAnalyticsOverview = () => {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Catégories d'analyse disponibles
  const analyticCategories = [
    {
      id: 'stock-rotation',
      name: 'Rotation des Stocks',
      description: 'Analyse des taux de rotation par produit avec graphiques comparatifs',
      icon: ArrowPathIcon,
      color: 'from-blue-600 to-indigo-600',
      path: '/advanced-analytics/stock-rotation'
    },
    {
      id: 'storage-costs',
      name: 'Coûts de Stockage',
      description: 'Analyse détaillée des coûts de possession et stockage par catégorie',
      icon: BuildingLibraryIcon,
      color: 'from-amber-600 to-orange-600',
      path: '/advanced-analytics/storage-costs'
    },
    {
      id: 'demand-forecast',
      name: 'Prévisions de Demande',
      description: 'Prévisions basées sur l\'historique des ventes et tendances du marché',
      icon: PresentationChartLineIcon,
      color: 'from-emerald-600 to-green-600',
      path: '/advanced-analytics/demand-forecast'
    },
    {
      id: 'seasonality',
      name: 'Analyse de Saisonnalité',
      description: 'Visualisation des tendances saisonnières et pics de demande',
      icon: CalendarDaysIcon, 
      color: 'from-purple-600 to-pink-600',
      path: '/advanced-analytics/seasonality'
    },
    {
      id: 'product-performance',
      name: 'Performance des Produits',
      description: 'Analyse détaillée des performances et de la rentabilité de votre catalogue',
      icon: ChartBarSquareIcon,
      color: 'from-green-600 to-teal-600',
      path: '/advanced-analytics/product-performance'
    },
    {
      id: 'market-comparison',
      name: 'Comparaison de Marché',
      description: 'Analyse comparative avec le marché et vos principaux concurrents',
      icon: GlobeEuropeAfricaIcon,
      color: 'from-blue-500 to-cyan-500',
      path: '/advanced-analytics/market-comparison'
    }
  ];

  const handleRefreshData = () => {
    setIsRefreshing(true);
    // Dans une application réelle, nous ferions un appel API pour actualiser les données
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  };

  const navigateTo = (path) => {
    navigate(path);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Analyses Avancées</h1>
          <p className="text-white/60 mt-2">
            Analyses et prévisions avancées pour optimiser votre gestion de stock
          </p>
        </div>
        <button
          onClick={handleRefreshData}
          disabled={isRefreshing}
          className="flex items-center space-x-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300"
        >
          <ArrowPathIcon className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span>{isRefreshing ? 'Actualisation...' : 'Actualiser'}</span>
        </button>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {analyticCategories.map((category, index) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => navigateTo(category.path)}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:border-white/30 cursor-pointer transition-all duration-300"
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white">{category.name}</h3>
                <p className="text-white/60 text-sm mt-2">{category.description}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${category.color}`}>
                <category.icon className="h-6 w-6 text-white" />
              </div>
            </div>
            
            <button className="mt-4 flex items-center text-white/70 hover:text-white transition-colors text-sm">
              <span>Voir l'analyse complète</span>
              <ArrowTrendingUpIcon className="h-4 w-4 ml-2" />
            </button>
          </motion.div>
        ))}
      </div>

      {/* Recent Reports */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <h3 className="text-xl font-semibold text-white mb-5">Dernières analyses générées</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5 rounded-lg">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Titre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Utilisateur</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              <tr className="hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">Analyse rotation Q2 2023</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">Rotation des stocks</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">20/06/2023</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">Ahmed K.</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <button className="p-2 text-white/60 hover:text-white transition-colors">
                    <DocumentArrowDownIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">Prévision demande S2 2023</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">Prévisions</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">15/06/2023</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">Sara L.</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <button className="p-2 text-white/60 hover:text-white transition-colors">
                    <DocumentArrowDownIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">Analyse coûts de stockage</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">Coûts de possession</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">12/06/2023</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">Karim M.</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <button className="p-2 text-white/60 hover:text-white transition-colors">
                    <DocumentArrowDownIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

const AdvancedAnalyticsPage = () => {
  return (
    <Routes>
      <Route path="/" element={<AdvancedAnalyticsOverview />} />
      <Route path="/stock-rotation" element={<StockRotationAnalysis />} />
      <Route path="/storage-costs" element={<StorageCostsAnalysis />} />
      <Route path="/demand-forecast" element={<DemandForecastAnalysis />} />
      <Route path="/seasonality" element={<SeasonalityAnalysis />} />
      <Route path="/product-performance" element={<ProductPerformanceAnalysis />} />
      <Route path="/market-comparison" element={<MarketComparisonAnalysis />} />
    </Routes>
  );
};

export default AdvancedAnalyticsPage; 